import { Component, OnInit } from '@angular/core';
import { Audit } from 'src/app/core/models/audit';
import { AdminService } from 'src/app/core/services/admin.service';
import { TableColumn } from 'src/app/shared/models/table-config.model';
import { TableLazyLoadEvent } from 'primeng/table';
import { finalize } from 'rxjs/operators';
import { DialogService } from 'primeng/dynamicdialog';
import { TextAreaModalComponent } from 'src/app/shared/components/text-area-modal/text-area-modal.component';

@Component({
  selector: 'app-audits',
  templateUrl: './audits.component.html',
  styleUrls: ['./audits.component.scss'],
  standalone: false,
  providers: [DialogService],
})
export class AuditsComponent implements OnInit {
  allAudits: Audit[] = []; // Todos os dados do banco
  visibleAudits: Audit[] = []; // Apenas a página atual (10 itens)
  tableData: any[] = [];

  totalItems = 0;
  loading: boolean = true;
  filter = '';

  // Agora podemos usar sortable: true pois o Front resolve a ordem!
  tableColumns: TableColumn[] = [
    {
      field: 'auditId',
      header: 'ID',
      type: 'text',
      width: '80px',
      sortable: true,
      linkAction: (row) => this.openAuditDetails(row),
    },
    {
      field: 'areaAccessed',
      header: 'AREA ACCESSED',
      type: 'text',
      width: 'auto',
      sortable: true,
    },
    {
      field: 'userName',
      header: 'USERNAME',
      type: 'text',
      width: '200px',
      sortable: true,
    },
    {
      field: 'ipaddress',
      header: 'IP ADDRESS',
      type: 'text',
      width: '150px',
      sortable: true,
    },
    {
      field: 'timestamp',
      header: 'DATE',
      type: 'date',
      width: '180px',
      sortable: true,
      dateFormat: 'dd/MM/yyyy HH:mm',
    },
  ];

  constructor(
    private adminService: AdminService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.loadAllAudits();
  }

  handleSearch(text: string) {
    this.filter = text?.toLowerCase() || '';
    this._updateTableData();
  }

  private _updateTableData(): void {
    let filtered = this.filter
      ? this.allAudits.filter((a) => this.matchesFilter(a))
      : [...this.allAudits];
    filtered.sort(
      (a: any, b: any) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    this.tableData = filtered;
  }

  // Busca TUDO de uma vez
  loadAllAudits() {
    this.loading = true;
    // Passamos vazio no filtro para trazer tudo e filtrar no front se quiser
    this.adminService
      .getAllAudits('')
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data: any) => {
          this.allAudits = data.audits || [];
          this.totalItems = this.allAudits.length;

          this._updateTableData();
        },
        error: () => {},
      });
  }

  // Chamado pela tabela quando muda página ou ordena
  loadAuditsLazy(event: TableLazyLoadEvent) {
    if (!this.allAudits.length) return;
    this.processLocalData(event);
  }

  // Lógica de Paginação/Ordenação Local (Igual Users/Roles)
  processLocalData(event: TableLazyLoadEvent) {
    this.loading = true;

    // 1. Filtro Local
    let filtered = this.allAudits;
    if (this.filter) {
      filtered = this.allAudits.filter((a) => this.matchesFilter(a));
    }
    this.totalItems = filtered.length;

    // 2. Ordenação Local
    if (event.sortField) {
      filtered.sort((a, b) => {
        const valueA = this.resolveFieldData(a, event.sortField as string);
        const valueB = this.resolveFieldData(b, event.sortField as string);
        let result = 0;

        if (valueA == null && valueB != null) result = -1;
        else if (valueA != null && valueB == null) result = 1;
        else if (valueA == null && valueB == null) result = 0;
        else if (typeof valueA === 'string' && typeof valueB === 'string')
          result = valueA.localeCompare(valueB);
        else result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;

        return (event.sortOrder || 1) * result;
      });
    } else {
      // Ordenação padrão por Data Decrescente (mais recente primeiro)
      filtered.sort(
        (a: any, b: any) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
    }

    // 3. Fatia para Paginação
    const first = event.first || 0;
    const rows = event.rows || 10;

    // Delay visual fake
    setTimeout(() => {
      this.visibleAudits = filtered.slice(first, first + rows);
      this.loading = false;
    }, 100);
  }

  private matchesFilter(data: any): boolean {
    const s = this.filter;
    return (
      String(data.auditId ?? '').includes(s) ||
      (data.areaAccessed && data.areaAccessed.toLowerCase().includes(s)) ||
      (data.userName && data.userName.toLowerCase().includes(s)) ||
      (data.ipaddress && data.ipaddress.toLowerCase().includes(s))
    );
  }

  private resolveFieldData(data: any, field: string): any {
    if (data && field) return data[field];
    return null;
  }

  private openAuditDetails(audit: Audit): void {
    const text = [
      `ID: ${audit.auditId}`,
      `Area Accessed: ${audit.areaAccessed ?? ''}`,
      `Username: ${audit.userName ?? ''}`,
      `IP Address: ${audit.ipaddress ?? ''}`,
      `Date: ${audit.timestamp ?? ''}`,
    ].join('\n');

    this.dialogService.open(TextAreaModalComponent, {
      showHeader: false,
      width: '520px',
      contentStyle: { padding: '0', borderRadius: '12px' },
      data: {
        title: `Audit Log #${audit.auditId}`,
        text,
        showSaveButton: false,
      },
    });
  }
}
